import React, { useState, useCallback, useMemo } from 'react';
import {
    Modal,
    Grid,
    Card,
    Text,
    Box,
    Button,
    Group,
    Badge,
    ActionIcon,
    TextInput,
} from '@mantine/core';
import { IconEdit, IconTrash, IconCode } from '@tabler/icons-react';
import JoditEditor from 'jodit-react';
import DOMPurify from 'dompurify';

// ==================== TYPES ====================

/**
 * Supported languages for the editor
 */
type SupportedLanguage = 'en' | 'ar';


/** DOMPurify configuration for sanitizing HTML content */
const DOMPURIFY_CONFIG = {
    ALLOWED_TAGS: [
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'a', 'img',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'blockquote', 'code', 'pre',
        'div', 'span', 'hr',
    ],
    ALLOWED_ATTR: [
        'href', 'target', 'rel',
        'src', 'alt', 'width', 'height',
        'style', 'class',
        'colspan', 'rowspan',
        'dir',  // For Arabic RTL
    ],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};


/**
 * Props for the TextFieldEditor component
 */
interface TextFieldEditorProps {
    /** Label for the editor field */
    label: string;
    /** Current HTML content value */
    value: string;
    /** Callback when content changes */
    onChange: (value: string) => void;
    /** Optional callback to clear/remove content */
    onRemove?: () => void;
    /** Language for editor interface and content direction */
    language?: SupportedLanguage;
    /** Placeholder text for empty state */
    placeholder?: string;
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Optional additional class name for the container */
    className?: string;
    /** Maximum character count (optional) */
    maxLength?: number;
    /** Optional error message */
    error?: string;
}

// ==================== CONSTANTS ====================
// zIndexConstants.ts
const Z_INDEX = {
    MODAL: 9999,
};
/**
 * Default placeholder text for empty state
 */
const DEFAULT_PLACEHOLDER = 'Click to add/edit rich text content';

/**
 * Default language
 */
const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * Modal size as percentage
 */
const MODAL_SIZE = '90%';

/**
 * Modal body height
 */
const MODAL_BODY_HEIGHT = '85vh';

/**
 * Right section width when both buttons are visible
 */
const RIGHT_SECTION_WIDTH_FULL = 70;

/**
 * Right section width when only edit button is visible
 */
const RIGHT_SECTION_WIDTH_MINIMAL = 40;

/**
 * Localized strings
 */
const STRINGS = {
    en: {
        richTextEditor: 'Rich Text Editor',
        editContentWithLivePreview: 'Edit content with live preview',
        hasContent: 'Has content',
        editor: 'Editor',
        autoPreview: 'Auto-Preview',
        cancel: 'Cancel',
        save: 'Save',
        startTyping: 'Start typing...',
        startTypingToSeePreview: 'Start typing to see preview...',
        editContent: 'Edit content',
        removeContent: 'Remove content',
    },
    ar: {
        richTextEditor: 'محرر النص المنسق',
        editContentWithLivePreview: 'قم بتحرير المحتوى ومعاينته مباشرة',
        hasContent: 'يحتوي على محتوى',
        editor: 'المحرر',
        autoPreview: 'معاينة تلقائية',
        cancel: 'إلغاء',
        save: 'حفظ',
        startTyping: 'ابدأ الكتابة هنا...',
        startTypingToSeePreview: 'ابدأ الكتابة لرؤية المعاينة...',
        editContent: 'تحرير المحتوى',
        removeContent: 'إزالة المحتوى',
    },
} as const;

/**
 * TextFieldEditor Component
 * 
 * A rich text editor component with live preview functionality using Jodit Editor.
 * 
 * @component
 * @example
 * ```tsx
 * <TextFieldEditor
 *   label="Description"
 *   value={description}
 *   onChange={(value) => setDescription(value)}
 *   onRemove={() => setDescription('')}
 *   language="en"
 * />
 * ```
 */
export const TextFieldEditor: React.FC<TextFieldEditorProps> = ({
    label,
    value,
    onChange,
    onRemove,
    language = DEFAULT_LANGUAGE,
    placeholder = DEFAULT_PLACEHOLDER,
    disabled = false,
    className,
    maxLength,
    error,
}) => {
    // ==================== STATE ====================
    const [modalOpened, setModalOpened] = useState<boolean>(false);
    const [localValue, setLocalValue] = useState<string>(value || '');

    const [previewHeight, setPreviewHeight] = useState<number>(400);
    const previewRef = React.useRef<HTMLDivElement>(null);

    const hasSetInitialHeight = React.useRef<boolean>(false);

    // ==================== EFFECTS ====================

    // Reset the flag when modal closes
    React.useEffect(() => {
        if (!modalOpened) {
            hasSetInitialHeight.current = false;
        }
    }, [modalOpened]);

    /**
     * Add global styles to fix Jodit popup interactions
     */
    React.useEffect(() => {
        if (modalOpened) {
            const styleId = 'jodit-popup-fix';
            let styleElement = document.getElementById(styleId) as HTMLStyleElement;

            if (!styleElement) {
                styleElement = document.createElement('style');
                styleElement.id = styleId;
                styleElement.innerHTML = `
                    .jodit-popup,
                    .jodit-popup *,
                    .jodit-ui-group,
                    .jodit-ui-group *,
                    .jodit-toolbar-button__trigger,
                    .jodit-color-picker,
                    .jodit-color-picker *,
                    .jodit-dialog,
                    .jodit-dialog *,
                    .jodit-dialog__panel,
                    .jodit-dialog__box {
                        pointer-events: auto !important;
                        cursor: pointer !important;
                    }
                    .jodit-popup__content {
                        pointer-events: auto !important;
                    }
                    .jodit-popup,
                    .jodit-dialog,
                    .jodit-dialog__panel,
                    .jodit-dialog__box {
                        z-index: 999999 !important;
                    }
                    .jodit-dialog__overlay {
                        z-index: 999998 !important;
                    }
                    
                    .jodit-wysiwyg {
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                    }
                    .jodit-wysiwyg table {
                        border-collapse: collapse;
                        width: 100%;
                        margin: 10px 0;
                    }
                    .jodit-wysiwyg table td,
                    .jodit-wysiwyg table th {
                        border: 1px solid #ddd;
                        padding: 8px;
                    }
                    .jodit-wysiwyg img {
                        max-width: 100%;
                        height: auto;
                    }
                    .jodit-wysiwyg h1, .jodit-wysiwyg h2, .jodit-wysiwyg h3,
                    .jodit-wysiwyg h4, .jodit-wysiwyg h5, .jodit-wysiwyg h6 {
                        margin: 10px 0;
                        font-weight: bold;
                    }
                    .jodit-wysiwyg h1 { font-size: 2em; }
                    .jodit-wysiwyg h2 { font-size: 1.5em; }
                    .jodit-wysiwyg h3 { font-size: 1.17em; }
                    .jodit-wysiwyg h4 { font-size: 1em; }
                    .jodit-wysiwyg h5 { font-size: 0.83em; }
                    .jodit-wysiwyg h6 { font-size: 0.67em; }
                    .jodit-wysiwyg p {
                        margin: 0 0 10px 0;
                    }
                    .jodit-wysiwyg ul, .jodit-wysiwyg ol {
                        padding-left: 40px;
                        margin: 10px 0;
                    }
                    .jodit-wysiwyg li {
                        margin: 5px 0;
                    }
                    .jodit-wysiwyg a {
                        color: #0066cc;
                        text-decoration: underline;
                    }
                    .jodit-wysiwyg blockquote {
                        border-left: 4px solid #ddd;
                        padding-left: 15px;
                        margin: 10px 0;
                        color: #666;
                    }
                    .jodit-wysiwyg code {
                        background: #f4f4f4;
                        padding: 2px 4px;
                        border-radius: 3px;
                        font-family: monospace;
                    }
                    .jodit-wysiwyg pre {
                        background: #f4f4f4;
                        padding: 10px;
                        border-radius: 3px;
                        overflow-x: auto;
                    }
                    .jodit-wysiwyg hr {
                        border: none;
                        border-top: 1px solid #ddd;
                        margin: 20px 0;
                    }
                `;
                document.head.appendChild(styleElement);
            }
        }
    }, [modalOpened]);


    // Measure preview height and sync with editor
    React.useEffect(() => {
        if (modalOpened && !hasSetInitialHeight.current) {
            const timer = setTimeout(() => {
                if (previewRef.current) {
                    const contentHeight = previewRef.current.scrollHeight;
                    setPreviewHeight(Math.max(400, contentHeight));
                    hasSetInitialHeight.current = true; // Mark as measured
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [modalOpened, localValue]);

    // ==================== COMPUTED VALUES ====================

    /**
     * Determines if the current language is Arabic
     */
    const isArabic = useMemo<boolean>(() => language === 'ar', [language]);

    /**
     * Determines if content exists
     */
    const hasContent = useMemo<boolean>(() => Boolean(value?.trim()), [value]);

    /**
     * Gets localized strings based on current language
     */
    const strings = useMemo(() => STRINGS[language] || STRINGS.en, [language]);

    /**
     * Calculates text direction based on language
     */
    const textDirection = useMemo<'ltr' | 'rtl'>(() => (isArabic ? 'rtl' : 'ltr'), [isArabic]);

    /**
     * Determines if remove button should be shown
     */
    const showRemoveButton = useMemo<boolean>(() => {
        return hasContent && Boolean(onRemove) && !disabled;
    }, [hasContent, onRemove, disabled]);

    /**
     * Calculates the right section width based on visible buttons
     */
    const rightSectionWidth = useMemo<number>(() => {
        return showRemoveButton ? RIGHT_SECTION_WIDTH_FULL : RIGHT_SECTION_WIDTH_MINIMAL;
    }, [showRemoveButton]);

    /**
     * Generates unique field ID for accessibility
     */
    const fieldId = useMemo<string>(() => {
        return `text-field-editor-${label.replace(/\s+/g, '-').toLowerCase()}`;
    }, [label]);

    /**
     * Jodit Editor Configuration
     */
    const editorConfig = useMemo(() => ({
        language: isArabic ? 'ar' : 'en',
        direction: textDirection,
        placeholder: strings.startTyping,
        toolbarAdaptive: false,
        buttons: [
            // Text Formatting 
            'bold', 'italic', 'underline', 'strikethrough',
            '|',
            // Font & Size
            'font', 'fontsize', 'brush', 'paragraph', '|',
            '|',
            // Other buttons
            'indent', 'outdent',
            'left', 'center', 'right', 'justify',
            'link', 'table', 'hr', 'symbols',
            'eraser', 'undo', 'redo',
            '|', 'source'
        ],
        minHeight: 400,
        maxHeight: previewHeight,
        height: previewHeight,
        showCharsCounter: false,
        showWordsCounter: false,
        showXPathInStatusbar: false,
        zIndex: 999999,
        popup: {
            zIndex: 999999,
        },
        allowResizeX: false,
        allowResizeY: false,
        toolbarSticky: false,
        toolbarDisableStickyForMobile: true,
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
    }), [isArabic, textDirection, strings.startTyping, previewHeight]);

    // ==================== EVENT HANDLERS ====================

    /**
     * Opens the editor modal and initializes local state with current value
     */
    const handleOpen = useCallback((): void => {
        if (!disabled) {
            setLocalValue(value || '');
            setModalOpened(true);
        }
    }, [value, disabled]);

    /**
     * Sanitizes HTML using DOMPurify
     */
    const sanitizeHTML = useCallback((html: string): string => {
        if (!html || typeof html !== 'string') {
            return '';
        }

        try {
            // Step 1: Parse with DOMParser
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Step 2: Process all <p> tags - split by <br>
            const paragraphs = doc.querySelectorAll('p');
            paragraphs.forEach(p => {
                const html = p.innerHTML;
                const parts = html.split(/<br\s*\/?>/i);

                if (parts.length > 1) {
                    // Create separate paragraphs for each part
                    const parent = p.parentNode;
                    parts.forEach(part => {
                        if (part.trim()) {
                            const newP = doc.createElement('p');
                            newP.innerHTML = part.trim();
                            parent?.insertBefore(newP, p);
                        }
                    });
                    p.remove();
                }
            });

            // Step 3: Get cleaned HTML
            const cleanedHTML = doc.body.innerHTML;

            // Step 4: DOMPurify for security
            const sanitized = DOMPurify.sanitize(cleanedHTML, {
                ...DOMPURIFY_CONFIG,
                KEEP_CONTENT: true,
            });

            // Step 5: Final cleanup
            return sanitized
                .replace(/<(p|div|span|strong|em|b|i|u)>\s*<\/\1>/gi, '')
                .replace(/\s{2,}/g, ' ')
                .trim();
        } catch (error) {
            console.error('Sanitization error:', error);
            return ''; // Return empty string on error to prevent XSS
        }
    }, []);

    /**
     * Saves the edited content with sanitization
     */
    const handleSave = useCallback((): void => {
        const sanitizedValue = sanitizeHTML(localValue);
        if (sanitizedValue !== '') {
            onChange(sanitizedValue);
            setModalOpened(false);
        } else {
            alert('The content cannot be empty or invalid. Please enter valid content.');
        }

    }, [localValue, onChange, sanitizeHTML]);

    /**
     * Cancels editing, discards changes, and closes the modal
     */
    const handleCancel = useCallback((): void => {
        setLocalValue(value || '');
        setModalOpened(false);
    }, [value]);

    /**
     * Handles the remove action with event propagation control
     */
    const handleRemove = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>): void => {
            e.stopPropagation();
            if (!disabled && onRemove) {
                onRemove();
            }
        },
        [onRemove, disabled]
    );

    /**
     * Handles the edit action with event propagation control
     */
    const handleEdit = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>): void => {
            e.stopPropagation();
            if (!disabled) {
                handleOpen();
            }
        },
        [handleOpen, disabled]
    );

    /**
     * Handles keyboard interaction for accessibility
     * Opens modal on Enter or Space key press
     */
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>): void => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                e.preventDefault();
                handleOpen();
            }
        },
        [handleOpen, disabled]
    );

    // ==================== RENDER HELPERS ====================

    /**
     * Renders the preview placeholder text
     */
    const previewPlaceholder = useMemo<string>(
        () =>
            `<p style="color: #adb5bd; text-align: center; padding: 20px;">${strings.startTypingToSeePreview}</p>`,
        [strings.startTypingToSeePreview]
    );

    /**
     * Memoized input styles based on content state
     */
    const inputStyles = useMemo(
        () => ({
            input: {
                cursor: disabled ? 'not-allowed' : 'pointer',
                backgroundColor: hasContent ? '#e7f5ff' : undefined,
                fontFamily: hasContent ? 'Monaco, Consolas, monospace' : undefined,
                fontSize: hasContent ? '13px' : undefined,
                opacity: disabled ? 0.6 : 1,
            },
        }),
        [hasContent, disabled]
    );

    /**
     * Gets the display value for the preview
     */
    const previewContent = useMemo<string>(() => {
        return sanitizeHTML(localValue || previewPlaceholder);
    }, [localValue, previewPlaceholder, sanitizeHTML]);

    // ==================== MAIN RENDER ====================

    return (
        <>
            {/* Input Field with Edit/Remove Actions */}
            <Box className={className}>
                {/* Label with Status Badge */}
                <Group gap="xs" mb={4}>
                    <Text
                        component="label"
                        size="sm"
                        fw={500}
                        htmlFor={fieldId}
                    >
                        {label}
                    </Text>
                    {hasContent && (
                        <Badge
                            size="xs"
                            color="blue"
                            variant="light"
                            aria-label={strings.hasContent}
                        >
                            {strings.hasContent}
                        </Badge>
                    )}
                </Group>

                {/* Read-Only Input */}
                <TextInput
                    id={fieldId}
                    placeholder={placeholder}
                    size="md"
                    readOnly
                    disabled={disabled}
                    error={error}
                    onClick={handleOpen}
                    onKeyDown={handleKeyDown}
                    styles={inputStyles}
                    aria-label={`${label} rich text editor. ${hasContent ? strings.hasContent : 'No content'}. Click to edit.`}
                    aria-describedby={error ? `error-${fieldId}` : undefined}
                    rightSection={
                        <Group gap={4} wrap="nowrap">
                            <ActionIcon
                                variant="subtle"
                                color="blue"
                                size="sm"
                                onClick={handleEdit}
                                disabled={disabled}
                                aria-label={`${strings.editContent} ${label}`}
                                title={strings.editContent}
                                tabIndex={-1}
                            >
                                <IconEdit size={16} />
                            </ActionIcon>

                            {showRemoveButton && (
                                <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    size="sm"
                                    onClick={handleRemove}
                                    disabled={disabled}
                                    aria-label={`${strings.removeContent} ${label}`}
                                    title={strings.removeContent}
                                    tabIndex={-1}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            )}
                        </Group>
                    }
                    rightSectionWidth={rightSectionWidth}
                />
            </Box>

            {/* Rich Text Editor Modal */}
            <Modal
                opened={modalOpened}
                onClose={handleCancel}
                title={
                    <Group gap="sm">
                        <Box
                            style={{
                                background: 'blue',
                                padding: '8px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                            aria-hidden="true"
                        >
                            <IconCode size={20} color="white" />
                        </Box>
                        <div>
                            <Text size="lg" fw={700}>
                                {strings.richTextEditor}
                            </Text>
                            <Text size="xs" c="dimmed">
                                {strings.editContentWithLivePreview}
                            </Text>
                        </div>
                    </Group>
                }
                size={MODAL_SIZE}
                radius="md"
                zIndex={Z_INDEX.MODAL}
                styles={{
                    body: {
                        height: MODAL_BODY_HEIGHT,
                        overflow: 'visible', // Needed for Jodit Editor dropdowns to render properly inside modal
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 0,
                    },
                    header: {
                        borderBottom: '1px solid #e9ecef',
                        padding: '20px 24px',
                        flexShrink: 0,
                    },
                    content: {
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        overflow: 'visible',
                    },
                    inner: {
                        padding: 0,
                    },
                }}
                closeButtonProps={{
                    'aria-label': strings.cancel,
                }}
                trapFocus={false}
                returnFocus
            >
                {/* Modal Content Container */}
                <Box
                    style={{
                        flex: 1,
                        overflow: 'auto',
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        height: '100%',
                    }}
                >
                    <Grid gap="lg" style={{ flex: 1, margin: 0, height: '100%', display: 'flex' }}>
                        {/* Left Column - Editor */}
                        <Grid.Col
                            span={{ base: 12, md: 6 }}
                            style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
                        >
                            <Box
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: 0,
                                    height: '100%',
                                }}
                            >
                                <Group mb="md" justify="space-between" style={{ flexShrink: 0 }}>
                                    <Text size="sm" fw={700}>
                                        {strings.editor}
                                    </Text>
                                </Group>
                                <Box
                                    dir={textDirection}
                                    style={{
                                        flex: 1,
                                        border: '1px solid #e9ecef',
                                        borderRadius: '8px',
                                        minHeight: 0,
                                        height: '100%',
                                        overflow: 'visible',
                                        position: 'relative',
                                        pointerEvents: 'auto',
                                    }}
                                    role="region"
                                    aria-label={`${strings.editor} ${label}`}
                                >
                                    <div style={{ pointerEvents: 'auto' }}>
                                        <JoditEditor
                                            value={localValue}
                                            config={editorConfig}
                                            onBlur={(newContent) => setLocalValue(newContent)}
                                            onChange={(newContent) => setLocalValue(newContent)}
                                        />
                                    </div>
                                </Box>
                            </Box>
                        </Grid.Col>

                        {/* Right Column - Live Preview */}
                        <Grid.Col
                            span={{ base: 12, md: 6 }}
                            style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
                        >
                            <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                <Group mb="md" justify="space-between" style={{ flexShrink: 0 }}>
                                    <Badge
                                        size="sm"
                                        variant="light"
                                        color="green"
                                        aria-label={strings.autoPreview}
                                    >
                                        {strings.autoPreview}
                                    </Badge>
                                </Group>
                                <Card
                                    ref={previewRef}
                                    withBorder
                                    p="md"
                                    style={{
                                        flex: 1,
                                        backgroundColor: 'white',
                                        overflow: 'auto',
                                        minHeight: 0,
                                    }}
                                    role="region"
                                    aria-label={`${strings.autoPreview} ${label}`}
                                    aria-live="polite"
                                >
                                    <Box
                                        dir={textDirection}
                                        dangerouslySetInnerHTML={{
                                            __html: previewContent,
                                        }}
                                    />
                                </Card>
                            </Box>
                        </Grid.Col>
                    </Grid>
                </Box>

                {/* Modal Footer with Action Buttons */}
                <Box
                    style={{
                        borderTop: '1px solid #e9ecef',
                        padding: '12px 20px',
                        background: 'white',
                        flexShrink: 0,
                    }}
                    role="toolbar"
                    aria-label="Editor actions"
                >
                    <Group justify="flex-end" gap="sm">
                        <Button
                            variant="default"
                            onClick={handleCancel}
                            size="md"
                            radius="md"
                            styles={{
                                root: { height: '42px', fontSize: '14px', fontWeight: 600 },
                            }}
                            aria-label={strings.cancel}
                        >
                            {strings.cancel}
                        </Button>
                        <Button
                            onClick={handleSave}
                            size="md"
                            variant="gradient"
                            gradient={{ from: 'blue', to: 'blue', deg: 135 }}
                            radius="md"
                            styles={{
                                root: { height: '42px', fontSize: '14px', fontWeight: 600 },
                            }}
                            aria-label={strings.save}
                        >
                            {strings.save}
                        </Button>
                    </Group>
                </Box>
            </Modal>
        </>
    );
};
